import S, { DataSignal } from 's-js';
import { IAjax, Request } from 'lib/Ajax';
import { ValidationWatcher, Errors, Error as ValidationError } from 'lib/validationWatcher';
import { RoleMapper } from 'common/services/rolemapper';
import TZDate from 'lib/TZDate';
import { Entrypoints } from 'core/app/entrypoints';
import { InviteUsersResponse, InviteUsersResponseData } from 'data/models/InviteUsersResponse';
import { SendUserInviteRequest, SendUserInviteRequestInvitedUser } from 'data/models/Accounts';
import { InviteUsersCache } from './cache';

export interface InvitedUserViewModel {
    Model : SendUserInviteRequestInvitedUser;
    Delete : () => void;
    HasOrImpliesRole : (roleId : number) => boolean;
    ImpliesOrMaterializesRole : (roleId : number) => boolean;
    ToggleRole : (roleId : number) => void;
    EmailValidation : () => ValidationError;
    index : DataSignal<number>;
    Key : number;
}

export interface InviteUsersParams {
    selectedOrganizationId? : number;
    onHidePopup : () => void;
}

export function InviteUsers(ajax : IAjax, entrypoints : Entrypoints, queryCache : InviteUsersCache, { selectedOrganizationId, onHidePopup } : InviteUsersParams) {
    let model = queryCache.InviteUsersResponses.update1(null, {}),
        request = ajax.Url<InviteUsersResponseData>(entrypoints.InviteUsers).Get(),
        invitation = new SendUserInviteRequest({
            OrganizationId: selectedOrganizationId || null
        }),
        client = ajax.Url<SendUserInviteRequest>(entrypoints.SendInvitation, {
            include: [i => i.InvitedUsers]
        }),
        validation = ValidationWatcher(client),
        sendInvitationHttpRequest = S.data<Request<SendUserInviteRequest> | null>(null),
        invitedUserKey = 0, // give our InvitedUserViewModels an auto-incrementing key for use by React
        invitedUsers = invitation.InvitedUsers.map<InvitedUserViewModel>((invitedUser, _, i) => InvitedUserViewModel(invitedUser, i), undefined, (items, mapped, from, to) => {
            for (let i = 0; i < from.length; i++) {
                mapped[from[i]].index(to[i]);
            }
        });

    request.done(data => {
        queryCache.InviteUsersResponses.update1(request.Date, data);
    });

    return {
        Model: model,
        Request: request,
        SendInvitationRequest: sendInvitationHttpRequest,
        Invitation: invitation,
        InvitedUsers: invitedUsers,
        Validation: validation,
        SelectDuplicateOrg,
        AddInvitedUser,
        Submit,
        HidePopup: onHidePopup
    };

    function SelectDuplicateOrg(orgId : number) {
        S.freeze(() => {
            invitation.OrganizationId(orgId);
            invitation.IsNewOrganization(false);
        });
    }

    function AddInvitedUser() {
        let _validation = Object.assign({} as Errors, S.sample(validation));
        delete _validation['InvitedUsers'];
        S.freeze(() => {
            validation(_validation);
            invitation.InvitedUsers.push(new SendUserInviteRequestInvitedUser());
        });
    }

    function Submit() {
        sendInvitationHttpRequest(client.Post(invitation));
    }

    function InvitedUserViewModel(invitedUser : SendUserInviteRequestInvitedUser, initialIndex : number) : InvitedUserViewModel {
        let roleMapper = RoleMapper(invitedUser.Roles),
            index = S.data(initialIndex),
            _validation = () => validation()[InvitedUserValidationKey(index())];

        return {
            Model: invitedUser,
            Delete: Delete,
            HasOrImpliesRole: roleMapper.HasOrImpliesRole,
            ImpliesOrMaterializesRole: roleMapper.ImpliesOrMaterializesRole,
            ToggleRole: roleId => roleMapper.ToggleRole(AddRole, RemoveRole, roleId),
            EmailValidation: _validation,
            index: index,
            Key: invitedUserKey++
        };

        function AddRole(id : number) {
            invitedUser.Roles.push(id);
        }

        function RemoveRole(id : number) {
            invitedUser.Roles.remove(id);
        }

        function Delete() {
            // map validation messages to correct invited user
            let i = S.sample(index),
                v = Object.assign({} as Errors, S.sample(validation)),
                invitedUserErrors = {} as { [key : number] : ValidationError },
                invitedUserErrorRegexp = /^InvitedUsers\[([0-9]+)\]\.Email$/;

            for (let key in v) {
                let match = invitedUserErrorRegexp.exec(key);
                if (match) {
                    invitedUserErrors[parseInt(match[1])] = v[key];
                }
            }

            for (let key in invitedUserErrors) {
                let j = parseInt(key);
                if (j >= i) delete v[InvitedUserValidationKey(j)];
                if (j > i) {
                    v[InvitedUserValidationKey(j - 1)] = invitedUserErrors[j];
                }
            }

            S.freeze(() => {
                invitation.InvitedUsers.remove(invitedUser);
                validation(v);
            });
        }
    }

    function InvitedUserValidationKey(i : number) {
        return 'InvitedUsers[' + i + '].Email';
    }
}
export type InviteUsers = ReturnType<typeof InviteUsers>;


// WEBPACK FOOTER //
// ./apps/portcall/inviteusers/model.ts